import React from "react";

/**
 * FlightRoute — one shared, correct flight-route renderer used everywhere a flight
 * is displayed in Booking Detail, so the arrows can never drift again.
 *
 * Follows the SUNSKY flight display spec:
 *   One Way (OW)          CGN → SAW           (blue outbound arrows)
 *     · with stops        CGN → ○ → SAW       (○ = stop / connection)
 *   Round Trip same route CGN → SAW ← CGN     (blue out →, blue return ←)
 *   Round Trip diff route CGN → SAW ← AYT     (GREEN arrows — return ends elsewhere)
 *   Multi-City (Multi)    CGN → SAW → AYT → BOJ (purple dotted segments)
 *
 * Data shape: legs[] with { departure|departureAirport, arrival|arrivalAirport,
 * direction ("Outbound"|"Inbound"), stops }, plus flightType ("OW"|"RT"|"Multi"/"MC"/"MF").
 */

const COLOR = { blue: "#2563eb", green: "#16a34a", purple: "#9333ea", ink: "#1a1d23" };

const up = (s) => String(s || "").toUpperCase();
const depOf = (l) => up(l.departure || l.departureAirport);
const arrOf = (l) => up(l.arrival || l.arrivalAirport);
// Number of ○ shown for a directional journey = connections between its segments
// plus any per-leg stop count (technical stops on a single segment).
const stopsIn = (grp) => Math.max(0, grp.length - 1) + grp.reduce((s, l) => s + (Number(l.stops) || 0), 0);

function splitLegs(legs, flightType) {
  const all = (legs || []).filter((l) => depOf(l) || arrOf(l));
  let inbound = all.filter((l) => (l.direction || "") === "Inbound");
  let outbound = all.filter((l) => (l.direction || "") !== "Inbound");
  const ft = up(flightType);
  const isRT = ft === "RT" || ft.startsWith("ROUND");
  // Fallback when the direction field isn't set: an RT with exactly two legs is
  // one outbound + one inbound.
  if (inbound.length === 0 && isRT && all.length === 2) {
    outbound = [all[0]];
    inbound = [all[1]];
  }
  return { all, outbound, inbound };
}

export default function FlightRoute({ legs = [], flightType = "OW", size = "md", style = {} }) {
  const { all, outbound, inbound } = splitLegs(legs, flightType);
  if (!all.length) return null;

  const ft = up(flightType);
  const isMulti = ["MULTI", "MC", "MF"].includes(ft);
  const isRT = !isMulti && (ft === "RT" || ft.startsWith("ROUND") || inbound.length > 0);

  const fs = size === "sm" ? 12 : 13.5;
  const dot = size === "sm" ? 7 : 8;
  const gap = size === "sm" ? 3 : 4;

  const Ap = (code, key) => (
    <span key={key} style={{ fontWeight: 700, color: COLOR.ink, letterSpacing: 0.2 }}>{code || "—"}</span>
  );
  const Ar = (color, left, key, dashed) => (
    <span key={key} aria-hidden style={{ color, fontWeight: 800, margin: `0 ${gap}px`, opacity: dashed ? 0.85 : 1, letterSpacing: dashed ? 1.5 : 0 }}>
      {dashed ? (left ? "⇠" : "⇢") : left ? "←" : "→"}
    </span>
  );
  const St = (color, key) => (
    <span
      key={key}
      title="Stop / connection"
      style={{ display: "inline-block", width: dot, height: dot, borderRadius: "50%", border: `1.6px solid ${color}`, margin: `0 ${gap}px`, verticalAlign: "middle", flex: "0 0 auto" }}
    />
  );

  const nodes = [];
  let k = 0;

  if (isMulti) {
    // Sequential chain A → B → C → D (purple, dotted arrows)
    const chain = [depOf(all[0]), ...all.map(arrOf)];
    nodes.push(Ap(chain[0], k++));
    for (let i = 1; i < chain.length; i++) {
      nodes.push(Ar(COLOR.purple, false, k++, true));
      nodes.push(Ap(chain[i], k++));
    }
  } else if (isRT) {
    const out = outbound.length ? outbound : all;
    const inb = inbound;
    const origin = depOf(out[0]);
    const mid = arrOf(out[out.length - 1]);
    const returnEnd = inb.length ? arrOf(inb[inb.length - 1]) : origin;
    const sameRoute = returnEnd === origin; // classic A→B, B→A
    const color = sameRoute ? COLOR.blue : COLOR.green; // green = round trip, different route
    // Outbound: origin →[○ →] mid
    nodes.push(Ap(origin, k++));
    for (let i = 0; i < stopsIn(out); i++) { nodes.push(Ar(color, false, k++)); nodes.push(St(color, k++)); }
    nodes.push(Ar(color, false, k++));
    nodes.push(Ap(mid, k++));
    // Return: mid ←[○ ←] returnEnd
    for (let i = 0; i < stopsIn(inb); i++) { nodes.push(Ar(color, true, k++)); nodes.push(St(color, k++)); }
    nodes.push(Ar(color, true, k++));
    nodes.push(Ap(returnEnd, k++));
  } else {
    // One Way: origin →[○ →] dest (blue)
    const grp = outbound.length ? outbound : all;
    const origin = depOf(grp[0]);
    const dest = arrOf(grp[grp.length - 1]);
    nodes.push(Ap(origin, k++));
    for (let i = 0; i < stopsIn(grp); i++) { nodes.push(Ar(COLOR.blue, false, k++)); nodes.push(St(COLOR.blue, k++)); }
    nodes.push(Ar(COLOR.blue, false, k++));
    nodes.push(Ap(dest, k++));
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", fontSize: fs, lineHeight: 1.4, ...style }}>
      {nodes}
    </span>
  );
}
